import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  ICognitoStorage,
} from 'amazon-cognito-identity-js';
import { getConfig } from './config';

let pool: CognitoUserPool | null = null;
let storage: ICognitoStorage | undefined;

/**
 * Cognito needs somewhere to persist tokens. React Native has no localStorage,
 * so each app injects its own (AsyncStorage on iOS, the same on macOS/Windows).
 * Must be called before any auth call.
 */
export function setAuthStorage(s: ICognitoStorage): void {
  storage = s;
  pool = null; // rebuild the pool so it picks up the new storage
}

function getPool(): CognitoUserPool {
  if (!pool) {
    const cfg = getConfig();
    pool = new CognitoUserPool({
      UserPoolId: cfg.userPoolId,
      ClientId: cfg.userPoolClientId,
      Storage: storage,
    });
  }
  return pool;
}

export class AuthError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = 'AuthError';
  }
}

/** Cognito's error strings are developer-facing; these are what AIM would say. */
function friendly(err: any): AuthError {
  const code = err?.code ?? err?.name;
  switch (code) {
    case 'NotAuthorizedException':
      return new AuthError(
        'The screen name or password you entered is incorrect. Please try again.',
        code
      );
    case 'UserNotFoundException':
      return new AuthError('That screen name was not found.', code);
    case 'UsernameExistsException':
      return new AuthError('That screen name is already taken.', code);
    case 'InvalidPasswordException':
      return new AuthError(
        'Passwords must be at least 10 characters and include a number.',
        code
      );
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return new AuthError('Too many attempts. Please wait a moment and try again.', code);
    case 'NetworkError':
      return new AuthError('Could not connect to the service. Check your connection.', code);
    default:
      return new AuthError(err?.message ?? 'Sign on failed. Please try again.', code);
  }
}

export interface Session {
  screenName: string;
  idToken: string;
  expiresAt: number;
}

function toSession(user: CognitoUser, s: CognitoUserSession): Session {
  return {
    screenName: user.getUsername(),
    idToken: s.getIdToken().getJwtToken(),
    expiresAt: s.getIdToken().getExpiration() * 1000,
  };
}

export function signIn(screenName: string, password: string): Promise<Session> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: screenName, Pool: getPool(), Storage: storage });
    user.authenticateUser(
      new AuthenticationDetails({ Username: screenName, Password: password }),
      {
        onSuccess: (s) => resolve(toSession(user, s)),
        onFailure: (err) => reject(friendly(err)),
        newPasswordRequired: () =>
          reject(new AuthError('This screen name requires a password reset.', 'NewPasswordRequired')),
      }
    );
  });
}

/**
 * The invite code rides along as clientMetadata, which the pre-signup Lambda
 * reads. It is never stored on the user record.
 *
 * `displayName` carries the capitalisation the user typed. Cognito lowercases
 * the username before any trigger sees it, so without this the screen name
 * would be flattened forever — AIM showed names as typed and matched them
 * case-insensitively.
 */
export function signUp(
  screenName: string,
  password: string,
  email: string,
  inviteCode: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    getPool().signUp(
      screenName,
      password,
      [new CognitoUserAttribute({ Name: 'email', Value: email })],
      [],
      (err) => (err ? reject(friendly(err)) : resolve()),
      {
        inviteCode: inviteCode.trim().toUpperCase(),
        displayName: screenName.trim(),
      }
    );
  });
}

/** Returns the cached session, refreshing it if the id token has expired. */
export function getSession(): Promise<Session | null> {
  return new Promise((resolve) => {
    const user = getPool().getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, s: CognitoUserSession | null) => {
      if (err || !s || !s.isValid()) return resolve(null);
      resolve(toSession(user, s));
    });
  });
}

export async function getIdToken(): Promise<string> {
  const s = await getSession();
  if (!s) throw new AuthError('You have been signed off. Please sign on again.', 'NoSession');
  return s.idToken;
}

export function signOut(): void {
  getPool().getCurrentUser()?.signOut();
}

export function forgotPassword(screenName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    new CognitoUser({ Username: screenName, Pool: getPool(), Storage: storage }).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(friendly(err)),
    });
  });
}

export function confirmPassword(
  screenName: string,
  code: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    new CognitoUser({ Username: screenName, Pool: getPool(), Storage: storage }).confirmPassword(
      code,
      newPassword,
      {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(friendly(err)),
      }
    );
  });
}
